import { useCallback, useEffect, useRef, useState } from 'react';
import {
    OptionStyles,
    ProductOption,
    ProductOptionChangedEvent,
    ProductOptionModel,
    ProductOptionsTranslations,
} from '@vp/product-options-ui';
import {
    Box,
    Button,
    FlexBox,
    Icon,
    IconButton,
    SelectionSet,
    SelectionSetInput,
    SelectionSetLabel,
    Spinner,
    Typography,
} from '@vp/swan';
import { usePrintProductConfiguratorQuery } from '../graphql/product-options.generated';
import {
    PRODUCT_OPTIONS_CHOICE_CUSTOMER,
    PRODUCT_OPTIONS_CHOICE_DESIGNER,
} from '../types/selectors';
import { HelpMeAnswerButton } from './HelpMeAnswerButton';
import type {
    ProductOptionsChoice,
    ProductOptionsData,
    ProductOptionsSelection,
} from '../types/selectors';

export interface ProductOptionsSelectorProps {
    data: ProductOptionsData;
    onComplete: (selection: ProductOptionsSelection) => void;
}

const MAX_VALUES_FOR_BUTTONS = 4;

function getOptionStyle(key: string, valuesCount: number): 'dropdown' | 'buttons' {
    if (key === 'Size') return 'dropdown';
    return valuesCount > MAX_VALUES_FOR_BUTTONS ? 'dropdown' : 'buttons';
}

export function ProductOptionsSelector({ data, onComplete }: ProductOptionsSelectorProps) {
    const hasPreselectedSelections =
        !!data.preselectedSelections && Object.keys(data.preselectedSelections).length > 0;
    const startAtStep2 =
        data.preselectedChoice === PRODUCT_OPTIONS_CHOICE_CUSTOMER || hasPreselectedSelections;

    const [currentStep, setCurrentStep] = useState(startAtStep2 ? 2 : 1);
    const [choice, setChoice] = useState<ProductOptionsChoice | null>(
        hasPreselectedSelections ? PRODUCT_OPTIONS_CHOICE_CUSTOMER : data.preselectedChoice ?? null
    );
    const [selections, setSelections] = useState<Record<string, string>>(
        data.preselectedSelections ?? {}
    );

    const totalSteps = choice === PRODUCT_OPTIONS_CHOICE_CUSTOMER ? 2 : 1;

    // Focus management: move focus to step heading on step changes
    const stepHeadingRef = useRef<HTMLHeadingElement>(null);
    const [prevStep, setPrevStep] = useState(startAtStep2 ? 2 : 1);
    useEffect(() => {
        if (currentStep !== prevStep) {
            setPrevStep(currentStep);
            requestAnimationFrame(() => {
                stepHeadingRef.current?.focus();
            });
        }
    }, [currentStep, prevStep]);

    const selectedAttributes = Object.entries(selections).map(([key, value]) => ({
        attributeKey: key,
        valueKey: value,
    }));

    const {
        data: configData,
        previousData,
        loading,
        error,
    } = usePrintProductConfiguratorQuery({
        variables: {
            input: {
                mpvId: data.mpvId,
                tenant: data.tenant,
                selectedAttributes,
            },
        },
        skip: currentStep !== 2,
        fetchPolicy: 'cache-and-network',
    });

    const productOptions =
        (configData ?? previousData)?.printProductConfigurator?.productOptions ?? [];

    const handleChoiceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setChoice(event.target.value as ProductOptionsChoice);
    };

    const handleNext = () => {
        if (!choice) return;
        if (choice === PRODUCT_OPTIONS_CHOICE_DESIGNER) {
            onComplete({ choice: PRODUCT_OPTIONS_CHOICE_DESIGNER });
        } else {
            setCurrentStep(2);
        }
    };

    const handleOptionSelect = useCallback((optionKey: string, valueKey: string) => {
        setSelections((prev) => ({
            ...prev,
            [optionKey]: valueKey,
        }));
    }, []);

    const handleContinue = () => {
        const selectionDetails = productOptions
            .filter((opt) => selections[opt.key])
            .map((opt) => {
                const selectedValue = opt.values.find((v) => v.key === selections[opt.key]);
                return {
                    optionName: opt.name,
                    valueName: selectedValue?.name ?? selections[opt.key],
                    imageUrl: selectedValue?.imageUrl || undefined,
                };
            });

        onComplete({
            choice: PRODUCT_OPTIONS_CHOICE_CUSTOMER,
            selections: Object.keys(selections).length > 0 ? selections : undefined,
            selectionDetails: selectionDetails.length > 0 ? selectionDetails : undefined,
        });
    };

    return (
        <Box
            style={{
                backgroundColor: 'var(--swan-sem-color-bg-active)',
                border: '1px solid var(--swan-sem-color-border-standard)',
                padding: 'var(--swan-sem-space-5)',
                borderRadius:
                    'var(--swan-sem-border-radius-strong) var(--swan-sem-border-radius-strong) 0 0',
            }}
        >
            {/* Header: Title + Pagination */}
            <FlexBox
                justifyContent="space-between"
                alignItems="center"
                style={{
                    minHeight: '44px',
                    marginBottom: 'var(--swan-sem-space-5)',
                }}
            >
                <Typography
                    ref={stepHeadingRef}
                    tabIndex={-1}
                    fontSkin="title-item"
                    style={{ outline: 'none' }}
                >
                    {currentStep === 1
                        ? 'Would you like to select your product specifications?'
                        : 'Select your product options'}
                </Typography>
                <FlexBox
                    alignItems="center"
                    gap={2}
                    style={{
                        visibility: totalSteps > 1 ? 'visible' : 'hidden',
                    }}
                >
                    <IconButton
                        skin="tertiary"
                        onClick={() => setCurrentStep(1)}
                        disabled={currentStep === 1}
                        aria-label="Previous step"
                        tooltip="Previous step"
                    >
                        <Icon iconType="chevronLeft" />
                    </IconButton>
                    <Typography fontSkin="body-small-bold" display={{ xs: 'none', sm: 'block' }}>
                        {currentStep} of {totalSteps}
                    </Typography>
                    <IconButton
                        skin="tertiary"
                        onClick={() => setCurrentStep(2)}
                        disabled={currentStep === 2}
                        aria-label="Next step"
                        tooltip="Next step"
                    >
                        <Icon iconType="chevronRight" />
                    </IconButton>
                </FlexBox>
            </FlexBox>

            {/* Step 1: Customer vs Designer choice */}
            {currentStep === 1 && (
                <SelectionSet
                    variant="single-select"
                    selectedValue={choice ?? undefined}
                    onChange={handleChoiceChange}
                    aria-label="Would you like to select your product specifications?"
                    style={{ gap: 'var(--swan-sem-space-3)' }}
                >
                    <SelectionSetInput value={PRODUCT_OPTIONS_CHOICE_CUSTOMER}>
                        <SelectionSetLabel
                            style={{
                                padding: 0,
                                ...(choice !== PRODUCT_OPTIONS_CHOICE_CUSTOMER && {
                                    backgroundColor: 'var(--swan-sem-color-bg-standard)',
                                }),
                            }}
                        >
                            <FlexBox
                                as="span"
                                alignItems="center"
                                gap={3}
                                style={{
                                    padding: 'var(--swan-sem-space-6) var(--swan-sem-space-5)',
                                }}
                            >
                                <Icon iconType="printedProducts" size="standard" />
                                <Typography fontSkin="body-standard-bold" as="span">
                                    I know my product specs
                                </Typography>
                            </FlexBox>
                        </SelectionSetLabel>
                    </SelectionSetInput>
                    <SelectionSetInput value={PRODUCT_OPTIONS_CHOICE_DESIGNER}>
                        <SelectionSetLabel
                            style={{
                                padding: 0,
                                ...(choice !== PRODUCT_OPTIONS_CHOICE_DESIGNER && {
                                    backgroundColor: 'var(--swan-sem-color-bg-standard)',
                                }),
                            }}
                        >
                            <FlexBox
                                as="span"
                                alignItems="center"
                                gap={3}
                                style={{
                                    padding: 'var(--swan-sem-space-6) var(--swan-sem-space-5)',
                                }}
                            >
                                <Icon iconType="workWithAPro" size="standard" />
                                <Typography fontSkin="body-standard-bold" as="span">
                                    Let my designer decide
                                </Typography>
                            </FlexBox>
                        </SelectionSetLabel>
                    </SelectionSetInput>
                </SelectionSet>
            )}

            {/* Step 2: Product options */}
            {currentStep === 2 && (
                <ProductOptionsTranslations locale="en-US">
                    <Box style={{ position: 'relative' }}>
                        {/* Loading overlay — keeps options visible, dims + blocks interaction */}
                        {loading && (
                            <Box
                                style={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 1000,
                                }}
                            >
                                <Spinner accessibleText="Loading product options" size="super" />
                            </Box>
                        )}
                        <Box
                            style={{
                                opacity: loading ? 0.5 : 1,
                                pointerEvents: loading ? 'none' : 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 'var(--swan-sem-space-5)',
                            }}
                        >
                            {error && productOptions.length === 0 && (
                                <Typography fontSkin="body-standard" color="error">
                                    Failed to load product options. You can continue without
                                    selecting.
                                </Typography>
                            )}
                            {productOptions.map((option) => {
                                const filteredValues = option.values.filter(
                                    (v) => v.key !== 'Custom'
                                );
                                const style = getOptionStyle(option.key, filteredValues.length);

                                return (
                                    <Box
                                        key={option.key}
                                        pb={style === 'buttons' ? 0 : 5}
                                        data-testid={`product-option-${option.key.toLowerCase()}`}
                                    >
                                        <ProductOption
                                            productOption={transformOptionToProductOption({
                                                option: {
                                                    key: option.key,
                                                    name: option.name,
                                                    values: filteredValues,
                                                },
                                                selectedKey: selections[option.key],
                                                style,
                                            })}
                                            tenant="vistaprint"
                                            currency=""
                                            fractionDigits={2}
                                            locale="en-US"
                                            priceDisplayMode="none"
                                            showIncompatibleChoices={true}
                                            skin="standard"
                                            onChanged={(event: ProductOptionChangedEvent) => {
                                                handleOptionSelect(
                                                    option.key,
                                                    event.choiceKey || ''
                                                );
                                            }}
                                            uiDensityUpdate={true}
                                        />
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </ProductOptionsTranslations>
            )}

            {/* Footer: Actions */}
            <FlexBox
                justifyContent="space-between"
                alignItems="center"
                gap={3}
                style={{
                    marginTop: 'var(--swan-sem-space-5)',
                }}
            >
                <HelpMeAnswerButton
                    selectorType="product-options"
                    options={
                        productOptions.length === 0
                            ? [
                                  { name: 'I know my product specs' },
                                  { name: 'Let my designer decide' },
                              ]
                            : productOptions.map((opt) => ({
                                  name: opt.name,
                                  values: opt.values
                                      .filter((v) => v.isCompatible)
                                      .map((v) => ({ name: v.name })),
                              }))
                    }
                />
                {currentStep === 1 ? (
                    <Button skin="secondary" onClick={handleNext} disabled={!choice}>
                        {choice === PRODUCT_OPTIONS_CHOICE_CUSTOMER ? 'Next' : 'Continue'}
                    </Button>
                ) : (
                    <Button skin="secondary" onClick={handleContinue} disabled={loading}>
                        Continue
                    </Button>
                )}
            </FlexBox>
        </Box>
    );
}

/* ─── Transform GraphQL response to ProductOptionModel ──────────────────── */

interface OptionForTransform {
    key: string;
    name: string;
    values: Array<{ key: string; name: string; isCompatible: boolean; imageUrl: string | null }>;
}

function transformOptionToProductOption({
    option,
    selectedKey,
    style,
}: {
    option: OptionForTransform;
    selectedKey: string | undefined;
    style: OptionStyles;
}): ProductOptionModel {
    return {
        key: option.key,
        name: option.name,
        style,
        choices: option.values.map((value) => ({
            key: value.key,
            name: value.name,
            isCompatible: value.isCompatible,
            image: value.imageUrl || undefined,
        })),
        selectedKey,
    };
}
